import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import { AlertCircle, RotateCcw, Home } from 'lucide-react';

export const ErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';
  let statusCode: number | null = null;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    switch (error.status) {
      case 404:
        title = 'Page not found';
        message = "The page you're looking for doesn't exist or has been moved.";
        break;
      case 401:
        title = 'Unauthorized';
        message = 'You need to be logged in to view this page.';
        break;
      case 403:
        title = 'Access denied';
        message = "You don't have permission to access this page.";
        break;
      case 500:
        title = 'Server error';
        message = 'Something went wrong on our end. Please try again later.';
        break;
      default:
        title = `Error ${String(error.status)}`;
        message = error.statusText || 'An error occurred.';
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  const handleGoHome = () => {
    void navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-dvh bg-white flex flex-col">
      <div className="flex-1 flex flex-col justify-center px-6">
        <div className="flex items-center gap-3 mb-6">
          <AlertCircle className="w-6 h-6 text-orange-500" aria-hidden="true" />
          {statusCode && (
            <span className="text-sm font-medium text-zinc-400">{statusCode}</span>
          )}
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-zinc-900 mb-2">
          {title}
        </h1>
        <p className="text-lg text-zinc-500 mb-8">{message}</p>

        <div className="space-y-3">
          <button
            onClick={handleGoHome}
            className="w-full py-4 px-6 rounded-xl font-medium text-lg bg-zinc-900 text-white hover:bg-zinc-800 active:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" aria-hidden="true" />
            Go to home
          </button>
          <button
            onClick={handleRefresh}
            className="w-full py-4 px-6 rounded-xl font-medium text-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 active:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" aria-hidden="true" />
            Try again
          </button>
        </div>
      </div>

      <div className="px-6 py-6 text-center text-sm text-zinc-300">
        LA Metro
      </div>
    </div>
  );
};

export default ErrorBoundary;
